import { MAX_SELECTED_LENGTH } from 'components/StoreSelector/hooks/useStoreSelector';
import { useReviewFollowedStoreListState } from 'hooks/business/conversation';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { uniqBy } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { atom, useAtom } from 'jotai';
import {
  getStatBody,
  newList,
  stat,
  updateStatus as updateStatusApi,
  UpdateStatusBody,
} from 'services/api/review';
import { searchStoreList, StoreInfo } from 'services/api/store';
import isEqual from 'react-fast-compare';
import { currentReviewProfileState, reviewListState } from './useReview';
import {
  defaultCursor,
  resetCursor,
  useLazadaSearch,
  useSetLazadaSearch,
  useSetShopeeSearch,
  useShopeeSearch,
  getDefaultFilte,
  useMercadoSearch,
  useSetMercadoSearch,
  useTikTokSearch,
  useTikTokUSSearch,
  useSetTikTokSearch,
  useSetTikTokUSSearch,
} from './useSearch';
import { useReviewStat, useSetReviewStat } from 'components/HorizontalNav/useReviewStatState';

export function useReview() {
  const [hasMore, setHasMore] = useState(true);

  const [cursor, setCursor] = useState(defaultCursor);

  const [list, setList] = useState<Review.Profile[]>([]);

  const [loading, setLoading] = useState(false);

  const fetchFlag = useRef(0);

  const fetchList = useCallback((body: Review.RequestSearch) => {
    fetchFlag.current += 1;

    const { current: currentFetchFlag } = fetchFlag;

    setLoading(true);

    return newList(body)
      .then((res) => {
        if (currentFetchFlag !== fetchFlag.current) {
          return;
        }

        const { reviewInfos, cursor } = res;

        batchedUpdates(() => {
          setHasMore(reviewInfos.length >= body.size);

          setCursor(cursor);

          setList((list) => {
            return uniqBy([...list, ...reviewInfos], 'commentId');
          });

          setLoading(false);
        });
      })
      .catch((err) => {
        if (currentFetchFlag === fetchFlag.current) {
          setLoading(false);
        }

        return Promise.reject(err);
      });
  }, []);

  return {
    cursor,
    hasMore,
    list,
    loading,
    setCursor,
    setHasMore,
    setList,
    setLoading,
    fetchList,
  };
}

export interface ReviewPlatformState {
  currentPlatform: Review.Platform | undefined;
  displayPlatformTab: boolean;
}

export const reviewPlatformState = atom<ReviewPlatformState>({
  currentPlatform: undefined,
  displayPlatformTab: false,
});

export const missingReviewListMapState = atom<Record<Review.Platform, Review.Profile[]>>({
  SHOPEE: [],
  LAZADA: [],
  MERCADO: [],
  TIKTOK: [],
  TIKTOK_US: [],
});

export function useGlobalReview() {
  const getPermission = useGetPermission();

  const fetchFlag = useRef(0);

  const [currentReview, setCurrentReview] = useAtom(currentReviewProfileState);

  const [reviewList, setReviewList] = useAtom(reviewListState);

  const followedStoreList = useReviewFollowedStoreListState();

  const [platformInfo, setPlatformInfo] = useAtom(reviewPlatformState);

  const { currentPlatform: platform } = platformInfo;

  const reviewStat = useReviewStat();
  const setReviewStat = useSetReviewStat();

  const shopeeSearch = useShopeeSearch();
  const lazadaSearch = useLazadaSearch();
  const mercadoSearch = useMercadoSearch();
  const tiktokSearch = useTikTokSearch();
  const tiktokUSSearch = useTikTokUSSearch();

  const setShopeeSearch = useSetShopeeSearch();
  const setLazadaSearch = useSetLazadaSearch();
  const setMercadoSearch = useSetMercadoSearch();
  const setTikTokSearch = useSetTikTokSearch();
  const setTikTokUSSearch = useSetTikTokUSSearch();

  const shopeeEnable = getPermission('访问评价聚合页', { platform: 'SHOPEE' });
  const lazadaEnable = getPermission('访问评价聚合页', { platform: 'LAZADA' });
  const mercadoEnable = getPermission('访问评价聚合页', { platform: 'MERCADO' });
  const tiktokEnable = getPermission('访问评价聚合页', { platform: 'TIKTOK' });
  const tiktokUSEnable = getPermission('访问评价聚合页', { platform: 'TIKTOK_US' });

  const [missingReviewListMap, setMissingReviewListMap] = useAtom(missingReviewListMapState);

  const fetchList = useCallback(
    (search: Review.Search) => {
      fetchFlag.current += 1;

      const { current: currentFetchFlag } = fetchFlag;

      setReviewList((preValue) => {
        return {
          ...preValue,
          loading: true,
        };
      });

      const request = (body: Review.RequestSearch) => {
        return newList(body)
          .then((res) => {
            if (currentFetchFlag !== fetchFlag.current) {
              return;
            }

            const { reviewInfos, cursor } = res;

            setReviewList((preValue) => {
              return {
                list: uniqBy([...preValue.list, ...reviewInfos], 'commentId'),
                loading: false,
                hasMore: reviewInfos.length >= body.size,
              };
            });

            if (cursor.length === 0) {
              return;
            }

            if (platform === 'SHOPEE') {
              setShopeeSearch({
                ...shopeeSearch,
                cursor,
              });
            }

            if (platform === 'LAZADA') {
              setLazadaSearch({
                ...lazadaSearch,
                cursor,
              });
            }

            if (platform === 'MERCADO') {
              setMercadoSearch({
                ...mercadoSearch,
                cursor,
              });
            }

            if (platform === 'TIKTOK') {
              setTikTokSearch({
                ...tiktokSearch,
                cursor,
              });
            }

            if (platform === 'TIKTOK_US') {
              setTikTokUSSearch({
                ...tiktokUSSearch,
                cursor,
              });
            }
          })
          .catch((err) => {
            if (currentFetchFlag === fetchFlag.current) {
              setReviewList((preValue) => {
                return {
                  ...preValue,
                  loading: false,
                };
              });
            }

            return Promise.reject(err);
          });
      };

      const { follower, ...requestSearch } = search;

      if (follower !== '1') {
        return request(requestSearch);
      }

      if (followedStoreList.length) {
        const storeIdList = followedStoreList.reduce<string[]>((res, store) => {
          const { externalType, storeId } = store;

          if (externalType === platform) {
            res.push(storeId);
          }

          return res;
        }, []);

        return request({
          ...requestSearch,
          storeId: storeIdList,
        });
      }

      return searchStoreList({
        follower: '1',
        platform,
        cursor: 0,
        page_size: MAX_SELECTED_LENGTH,
      }).then((res) => {
        return request({
          ...requestSearch,
          storeId: res.data.data.data.map((store) => store.storeId),
        });
      });
    },
    [
      setReviewList,
      followedStoreList,
      platform,
      setShopeeSearch,
      shopeeSearch,
      setLazadaSearch,
      lazadaSearch,
      setMercadoSearch,
      mercadoSearch,
      setTikTokSearch,
      tiktokSearch,
      setTikTokUSSearch,
      tiktokUSSearch,
    ],
  );

  const updateStatus = useCallback(
    (body: UpdateStatusBody) => {
      return updateStatusApi(body).then(() => {
        setReviewList((preValue) => {
          return {
            ...preValue,
            list: preValue.list.map((profile) => {
              if (profile.commentId !== body.commentId) {
                return profile;
              }

              return {
                ...profile,
                status: body.status,
              };
            }),
          };
        });
        stat(getStatBody())
          .then(setReviewStat)
          .catch(() => {});
      });
    },
    [setReviewList, setReviewStat],
  );

  const resetGlobalList = useCallback(
    (shouldResetCursor = false, shouldUnsetConv = true) => {
      if (shouldUnsetConv) {
        setCurrentReview(null);
      }

      setReviewList((preValue) => {
        return {
          ...preValue,
          list: [],
          hasMore: true,
        };
      });

      if (shouldResetCursor) {
        setShopeeSearch(resetCursor(shopeeSearch));

        setLazadaSearch(resetCursor(lazadaSearch));

        setMercadoSearch(resetCursor(mercadoSearch));

        setTikTokSearch(resetCursor(tiktokSearch));

        setTikTokUSSearch(resetCursor(tiktokUSSearch));
      }
    },
    [
      setReviewList,
      setCurrentReview,
      setShopeeSearch,
      shopeeSearch,
      setLazadaSearch,
      lazadaSearch,
      setMercadoSearch,
      mercadoSearch,
      setTikTokSearch,
      tiktokSearch,
      setTikTokUSSearch,
      tiktokUSSearch,
    ],
  );

  const changeFilte = (filte: Review.Filte, shouldResetCursor = false, shouldUnsetConv = true) => {
    if (platform === 'SHOPEE') {
      const { size, cursor } = shopeeSearch;

      setShopeeSearch(
        resetCursor({
          ...filte,
          size,
          cursor,
        }),
      );

      if (isEqual(filte, getDefaultFilte('shopee'))) {
        setMissingReviewListMap((pre) => {
          return {
            ...pre,
            SHOPEE: [],
          };
        });
      }
    }

    if (platform === 'LAZADA') {
      const { size, cursor } = lazadaSearch;
      setLazadaSearch(
        resetCursor({
          ...filte,
          size,
          cursor,
        }),
      );

      if (isEqual(filte, getDefaultFilte('lazada'))) {
        setMissingReviewListMap((pre) => {
          return {
            ...pre,
            LAZADA: [],
          };
        });
      }
    }

    if (platform === 'MERCADO') {
      const { size, cursor } = mercadoSearch;
      setMercadoSearch(
        resetCursor({
          ...filte,
          size,
          cursor,
        }),
      );

      if (isEqual(filte, getDefaultFilte('mercado'))) {
        setMissingReviewListMap((pre) => {
          return {
            ...pre,
            MERCADO: [],
          };
        });
      }
    }

    if (platform === 'TIKTOK') {
      const { size, cursor } = tiktokSearch;
      setTikTokSearch(
        resetCursor({
          ...filte,
          size,
          cursor,
        }),
      );

      if (isEqual(filte, getDefaultFilte('tiktok'))) {
        setMissingReviewListMap((pre) => {
          return {
            ...pre,
            TIKTOK: [],
          };
        });
      }
    }

    if (platform === 'TIKTOK_US') {
      const { size, cursor } = tiktokUSSearch;
      setTikTokUSSearch(
        resetCursor({
          ...filte,
          size,
          cursor,
        }),
      );

      if (isEqual(filte, getDefaultFilte('tiktok_us'))) {
        setMissingReviewListMap((pre) => {
          return {
            ...pre,
            TIKTOK_US: [],
          };
        });
      }
    }

    resetGlobalList(shouldResetCursor, shouldUnsetConv);
  };

  const changeFiltePartial = (filtePartial: Partial<Review.Filte>) => {
    if (platform) {
      const searchConfig = {
        SHOPEE: shopeeSearch,
        LAZADA: lazadaSearch,
        MERCADO: mercadoSearch,
        TIKTOK: tiktokSearch,
        TIKTOK_US: tiktokUSSearch,
      }[platform];

      const { size, cursor, ...filte } = searchConfig;

      changeFilte({ ...filte, ...filtePartial }, false, false);
    }
  };

  const refsCurrent = {
    setPlatformInfo,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    if (platform) {
      return;
    }

    if (!shopeeEnable && !lazadaEnable && !mercadoEnable && !tiktokEnable && !tiktokUSEnable) {
      return;
    }

    if (shopeeEnable) {
      refs.current.setPlatformInfo({
        currentPlatform: 'SHOPEE',
        displayPlatformTab: lazadaEnable || mercadoEnable || tiktokEnable || tiktokUSEnable,
      });
      return;
    }
    if (lazadaEnable) {
      refs.current.setPlatformInfo({
        currentPlatform: 'LAZADA',
        displayPlatformTab: mercadoEnable || tiktokEnable || tiktokUSEnable,
      });
      return;
    }
    if (mercadoEnable) {
      refs.current.setPlatformInfo({
        currentPlatform: 'MERCADO',
        displayPlatformTab: tiktokEnable || tiktokUSEnable,
      });
      return;
    }
    if (tiktokEnable) {
      refs.current.setPlatformInfo({
        currentPlatform: 'TIKTOK',
        displayPlatformTab: tiktokUSEnable,
      });
      return;
    }
    if (tiktokUSEnable) {
      refs.current.setPlatformInfo({
        currentPlatform: 'TIKTOK_US',
        displayPlatformTab: false,
      });
      return;
    }
  }, [
    platform,
    getPermission,
    shopeeEnable,
    lazadaEnable,
    mercadoEnable,
    tiktokEnable,
    tiktokUSEnable,
  ]);

  return {
    mercadoEnable,
    currentReview,
    setCurrentReview,
    reviewList,
    setReviewList,
    fetchList,
    updateStatus,
    shopeeSearch,
    lazadaSearch,
    mercadoSearch,
    setShopeeSearch,
    setLazadaSearch,
    setMercadoSearch,
    platformInfo,
    setPlatformInfo,
    resetGlobalList,
    changeFilte,
    changeFiltePartial,
    missingReviewListMap,
    setMissingReviewListMap,
    followedStoreList,
    reviewStat,
    setReviewStat,
    tiktokSearch,
    tiktokUSSearch,
    setTikTokSearch,
    setTikTokUSSearch,
  };
}

export function isValidReview(
  review: Review.Profile,
  detail: Review.Detail,
  search: Review.Search,
  followedStoreList: StoreInfo[],
) {
  const {
    platform,
    region,
    follower,
    storeId,
    reviewStartTime,
    reviewEndTime,
    reviewRating,
    productRating,
    sellerRating,
    logisticsRating,
    handleStatus,
    hasContent,
  } = search;

  if (
    (platform === 'shopee' && detail.channel !== 1) ||
    (platform === 'lazada' && detail.channel !== 2)
  ) {
    return false;
  }

  if (region && region !== detail.region) {
    return false;
  }

  if (follower === '1') {
    const followedStoreIdList = followedStoreList.map(({ storeId }) => storeId);

    if (!followedStoreIdList.includes(detail.internalStoreId)) {
      return false;
    }
  }

  if (storeId && storeId.length && !storeId.includes(detail.internalStoreId)) {
    return false;
  }

  if (
    reviewStartTime &&
    reviewEndTime &&
    (reviewStartTime > review.commentCreateTime || reviewEndTime < review.commentCreateTime)
  ) {
    return false;
  }

  if (reviewRating && reviewRating.length && !reviewRating.includes(`${review.star}`)) {
    return false;
  }

  // lazada 三项评分任一符合就算筛选命中
  if (
    platform === 'lazada' &&
    productRating &&
    productRating.length &&
    !productRating.includes(`${review.productStar}`) &&
    sellerRating &&
    sellerRating.length &&
    !sellerRating.includes(`${review.sellerStar}`) &&
    logisticsRating &&
    logisticsRating.length &&
    !logisticsRating.includes(`${review.logisticsStar}`)
  ) {
    return false;
  }

  if (handleStatus && handleStatus.length && !handleStatus.includes(`${review.status}`)) {
    return false;
  }

  if (hasContent === 'all') {
    return true;
  }

  const reviewHasContent = detail.buyerComment || detail.imageUrls || detail.videoUrls;

  if ((reviewHasContent && hasContent === '1') || (!reviewHasContent && hasContent === '0')) {
    return true;
  }

  return false;
}

export function getInsertIndex(
  review: Review.Profile,
  cursor: Review.Cursor,
  list: Review.Profile[],
) {
  const { fieldName, direction } = cursor;

  const { commentCreateTime, star, buyerComment, imageUrls, videoUrls } = review;

  if (fieldName === 'comment_time') {
    return list.findIndex((item) => {
      return direction === 'ASC'
        ? item.commentCreateTime >= commentCreateTime
        : item.commentCreateTime <= commentCreateTime;
    });
  }

  if (fieldName === 'review_rating') {
    return list.findIndex((item) => {
      return direction === 'ASC' ? item.star >= star : item.star <= star;
    });
  }

  if (fieldName === 'has_content') {
    const hasContent = buyerComment || imageUrls || videoUrls;

    return list.findIndex((item) => {
      const curHasContent = item.buyerComment || item.imageUrls || item.videoUrls;

      return direction === 'ASC' ? !hasContent || curHasContent : hasContent || !curHasContent;
    });
  }

  return -1;
}

export const reviewTrade = atom<Conversation.Trade.TradeWithLogistics | null>(null);
